import {
  darken,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
  useMediaQuery
} from '@material-ui/core';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import PrintLabelProductOutlineIcon from 'images/icons/objects/printers/printerYellowOutline.svg';
import PrintLabelProductOutlineIconDisabled from 'images/icons/objects/printers/printerLightGrayBgOutline.svg';
import ClearedLabelIcon from 'images/icons/signals/removesAndDeletes/trashIconNavyBlueLightGrayBgRoundedLightBlueBorder.svg';
import ClearedLabelIconDisabled from 'images/icons/signals/removesAndDeletes/trashIconLightGrayRoundedLightGrayBorder.svg';
import GroupedLabelsIcon from 'images/icons/labels/groupedLabels.svg';

const getStatusColor = (status) => {
  switch (status) {
    case 'MISSING':
      return '#888888';
    case 'EXPIRED':
      return '#f53d4c';
    case 'ON_DATE':
      return '#f7d56e';
    case 'VALID':
      return '#8cf295';
    default:
      return '#f7d56e';
  }
};

const mapRowToLabelItem = (row, product) => ({
  productDescription:
    row?.productDescription || row?.description || product?.description,
  productImageUrl: row?.image?.url || product?.image?.url,
  quantity:
    typeof row?.quantity === 'number'
      ? row.quantity
      : typeof row?.labelQuantity === 'string'
        ? parseFloat(row.labelQuantity.replace(',', '.'))
        : parseFloat(
          String(row?.quantity || '0')
            .toString()
            .replace(',', '.')
        ),
  manipulatedAt: row?.manipulatedAt || row?.createdAt,
  validAt: row?.validAt || row?.expirationAt,
  conservation: row?.conservation || product?.conservation,
   unitAbbreviation:
    row?.consumptionUnitAbbreviation ??
    product?.consumptionUnitsPrimary?.abbreviation ??
    ''
});

function StockExtractTable({
  extract = [],
  isLoading = false,
  printLabel,
  clearLabel,
  product,
  setModalSettings,
  setInternalModalSettings,
  onRowClick
}) {
  const isMobileScreen = useMediaQuery('(max-width: 866px)');

  return (
    <div
      style={{
        flex: 1,
        flexBasis: 0,
        borderRadius: 5,
        overflow: 'auto',
        fontFamily: 'sans-serif',
        backgroundColor: '#ffffff',
        marginTop: '10px'
      }}
    >
      <table width="100%">
        <thead
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: '#D0D3E2',
            color: '#1F2445'
          }}
        >
          <tr>
            <th
              className={`fontSizeDefault ${isMobileScreen ? 'textAlignCenter' : 'textAlignLeft'
                }`}
              style={{
                width: isMobileScreen ? '15%' : '24%',
                padding: isMobileScreen ? '4px 5px' : '4px 10px'
              }}
            >
              DATA
            </th>
            <th
              className="fontSizeDefault textAlignRight"
              style={{
                width: isMobileScreen ? '38%' : '33%',
                padding: isMobileScreen ? '4px 5px' : '4px 10px'
              }}
            >
              QTDE
            </th>
            <th
              className="fontSizeDefault textAlignRight"
              style={{
                width: isMobileScreen ? '32%' : '33%',
                padding: isMobileScreen ? '4px 5px' : '4px 10px'
              }}
            >
              VALIDADE
            </th>
            <th
              className="fontSizeDefault textAlignCenter"
              style={{
                width: isMobileScreen ? '15%' : '10%',
                padding: isMobileScreen ? '4px 5px' : '4px 10px',
                borderLeft: 'solid 1px #454A67'
              }}
            >
              BAIXA
            </th>
          </tr>
        </thead>

        <tbody>
          {isLoading && (
            <tr
              style={{ position: 'sticky', top: 25, backgroundColor: '#fff' }}
            >
              <td colSpan={4}>
                <LinearProgress />
              </td>
            </tr>
          )}

          {!extract?.length && (
            <tr>
              <td
                colSpan={4}
                className="textAlignCenter italic"
                style={{ padding: '8px', color: '#666' }}
              >
                Nada encontrado
              </td>
            </tr>
          )}

          {extract.map((row, index) => {
            const date = new Date(row.createdAt);
            const validAt = new Date(row.validAt);
            const isDisabledStatus = row?.isLabelCleared
              ? row.labelStatus
              : 'MISSING';
            const validStatusColor = getStatusColor(isDisabledStatus);
            const showGroupedCard =
              Number(row?.notClearedCount) + Number(row?.clearedCount) !== 1;

            const openDetails = () => {
              if (!onRowClick) return;
              const labelItem = mapRowToLabelItem(row, product);
              onRowClick(labelItem);
            };

            return (
              <tr
                key={row.id}
                style={{
                  backgroundColor: index % 2 === 1 ? '#F4F5FB' : '#FFF',
                  borderBottom: '1px solid #e0e0e0',
                  fontSize: 14,
                  cursor: onRowClick ? 'pointer' : 'default'
                }}
                onClick={onRowClick ? openDetails : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                      if (e.key === 'Enter') openDetails();
                    }
                    : undefined
                }
                tabIndex={onRowClick ? 0 : -1}
                role={onRowClick ? 'button' : undefined}
              >
                <td
                  className={`fontSizeDefault ${isMobileScreen ? 'textAlignCenter' : 'textAlignLeft'
                    }`}
                  style={{ padding: isMobileScreen ? '5px' : '5px 10px' }}
                >
                  <Typography
                    variant="inherit"
                    style={{
                      fontWeight: 600,
                      width: isMobileScreen ? 'auto' : '38%'
                    }}
                  >
                    {isMobileScreen
                      ? format(date, 'dd/MM', { locale: ptBR })
                      : format(date, 'dd/MM/yyyy - EEEE', {
                        locale: ptBR
                      }).replace('-feira', '')}
                  </Typography>
                </td>

                <td
                  style={{
                    padding: isMobileScreen ? '5px' : '5px 10px',
                    borderLeft: 'solid 1px #D0D3E2'
                  }}
                >
                  <div className="rightInfos dFlex alignEnd flexColumn">
                    {showGroupedCard ? (
                      <div className="groupedLabelInfos dFlex justifyEnd alignCenter">
                        <img
                          src={GroupedLabelsIcon}
                          alt="Grouped labels"
                          style={{ height: 20, width: 20 }}
                        />
                        <span
                          className="bold ml5"
                          style={{ color: '#0E1337', fontSize: 12 }}
                        >
                          {row?.notClearedCount}/
                          {row?.clearedCount + row?.notClearedCount}
                        </span>
                      </div>
                    ) : null}

                    <Typography variant="inherit">
                      {Number(row.quantity).toFixed(3).replace('.', ',')}
                      {product?.consumptionUnitsPrimary?.abbreviation}
                    </Typography>
                  </div>
                </td>

                <td
                  style={{
                    padding: isMobileScreen ? '5px' : '5px 10px',
                    borderLeft: 'solid 1px #D0D3E2'
                  }}
                >
                  <div className="dFlex alignCenter justifyBetween">
                    <IconButton
                      style={{
                        padding: 6,
                        backgroundColor: !row?.isLabelCleared
                          ? '#F2F3F8'
                          : '#5062F0',
                        borderRadius: 8,
                        border: !row?.isLabelCleared
                          ? 'solid 1px #D0D3E2'
                          : 'transparent',
                        height: 35,
                        width: 35,
                        boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.15)'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!row?.isLabelCleared) return;

                        if (showGroupedCard) {
                          setInternalModalSettings((prev) =>
                            prev.map((modal) =>
                              modal.name === 'printQuantityDialog'
                                ? { ...modal, open: true, labelItem: row }
                                : modal
                            )
                          );
                          return;
                        }

                        printLabel({ ...row, numberOfLabels: 1 });
                      }}
                    >
                      <img
                        src={
                          !row?.isLabelCleared
                            ? PrintLabelProductOutlineIconDisabled
                            : PrintLabelProductOutlineIcon
                        }
                        alt="Print"
                        style={{ height: 16 }}
                      />
                    </IconButton>

                    <Typography
                      variant="inherit"
                      className={`${row?.isLabelCleared ? 'bold' : ''}`}
                      style={{ color: darken(validStatusColor, 0.3) }}
                    >
                      {isMobileScreen ? (
                        <div className="dFlex flexColumn">
                          <span style={{ lineHeight: 1.3 }}>
                            {format(validAt, 'dd/MM')}
                          </span>
                          <span style={{ lineHeight: 1.3 }}>
                            {format(validAt, 'HH:mm')}
                          </span>
                        </div>
                      ) : (
                        format(validAt, 'dd/MM/yyyy - HH:mm')
                      )}
                    </Typography>
                  </div>
                </td>

                <td
                  className="textAlignCenter"
                  style={{
                    padding: isMobileScreen ? '5px' : '5px 10px',
                    borderLeft: 'solid 1px #454A67'
                  }}
                >
                  <Tooltip
                    title={
                      <div style={{ padding: '4px' }}>
                        <p style={{ margin: 0 }}>Efetuar Baixa</p>
                      </div>
                    }
                    aria-label="filters"
                    placement="right-center"
                    disableHoverListener={!row?.isLabelCleared}
                  >
                    <IconButton
                      style={{
                        borderRadius: 8,
                        height: 35,
                        width: 35,
                        boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.15)'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!row?.isLabelCleared) return;

                        if (row?.batchId && Number(row?.notClearedCount) > 1) {
                          setInternalModalSettings((prev) =>
                            prev.map((modal) =>
                              modal.name === 'labelPrintWriteOffDialog'
                                ? { ...modal, open: true, labelItem: row }
                                : modal
                            )
                          );
                          return;
                        }

                        setInternalModalSettings((prev) =>
                          prev.map((modal) =>
                            modal.name === 'confirmDialog'
                              ? {
                                ...modal,
                                open: true,
                                product,
                                title: `${format(
                                  validAt,
                                  'dd/MM/yyyy - HH:mm'
                                )}`,
                                content:
                                  'Deseja realmente efetuar a baixa desta etiqueta?',
                                onConfirm: () => clearLabel(row),
                                onClose: () =>
                                  setInternalModalSettings((prev2) =>
                                    prev2.map((m) =>
                                      m.name === 'confirmDialog'
                                        ? {
                                          ...m,
                                          open: false,
                                          product: null,
                                          title: '',
                                          content: '',
                                          onConfirm: '',
                                          onClose: ''
                                        }
                                        : m
                                    )
                                  )
                              }
                              : modal
                          )
                        );
                      }}
                    >
                      <img
                        src={
                          !row?.isLabelCleared
                            ? ClearedLabelIconDisabled
                            : ClearedLabelIcon
                        }
                        alt="Clear Label Icon"
                        style={{ height: 35, width: 35 }}
                      />
                    </IconButton>
                  </Tooltip>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default StockExtractTable;
